import { useParams, Link, Navigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PostDeployControl } from "@/components/PostDeployControl";
import { ArrowLeft } from "lucide-react";
import { AUTHORED_POSTS } from "@/data/blogContent";

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch (e) {
    return "";
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = AUTHORED_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div data-testid="blog-post-page" className="bg-white">
      <SiteHeader />
      <main>
        {/* Post hero */}
        <section className="relative overflow-hidden bg-[#0b0b0c]">
          <img src={post.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(2,2,2,0.7) 0%, rgba(2,2,2,0.88) 100%)" }} />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-36 pb-16">
            <Link to="/blog" data-testid="blog-post-back-link" className="inline-flex items-center gap-2 font-nunito text-[12px] font-extrabold uppercase tracking-[0.12em] text-white/70 hover:text-white transition-colors duration-150">
              <ArrowLeft size={14} /> Back to blog
            </Link>
            <div className="mt-6 flex items-center gap-2 text-[11px] font-nunito font-extrabold uppercase tracking-[0.14em] text-[#ff6b63]">
              <span>{post.category}</span>
              <span className="text-white/40">·</span>
              <span className="text-white/70">{fmtDate(post.date)}</span>
              <span className="text-white/40">·</span>
              <span className="text-white/70">{post.readMinutes} min read</span>
            </div>
            <h1 data-testid="blog-post-title" className="mt-4 font-nunito font-extrabold text-white text-3xl sm:text-4xl leading-[1.15] tracking-tight">
              {post.title}
            </h1>
            <div className="mt-6">
              <PostDeployControl slug={post.slug} title={post.title} />
            </div>
          </div>
        </section>

        {/* Article body */}
        <article data-testid="blog-post-body" className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          {post.sections.map((s, i) =>
            s.type === "h2" ? (
              <h2 key={i} className="mt-10 font-nunito text-xl sm:text-2xl font-extrabold text-[#383838] heading-redul pb-3">
                {s.text}
              </h2>
            ) : (
              <p key={i} className="mt-5 text-[15px] text-[#4d4d4d] leading-[1.85]">
                {s.text}
              </p>
            )
          )}

          {/* Inline CTA */}
          <div className="mt-12 rounded-[14px] bg-[#0b0b0c] p-8 text-center">
            <h3 className="font-nunito text-xl font-extrabold text-white">See what AI engines say about your brand.</h3>
            <p className="mt-3 text-[14px] text-[#bdbdbd] leading-relaxed">
              Free Visibility Audit — rankings, AI citations and your fastest wins, in 2 business days.
            </p>
            <Link
              to="/#visibility-audit"
              data-testid="blog-post-cta-button"
              className="mt-6 inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#d92820] transition-colors duration-150"
            >
              Get My Free Visibility Audit
            </Link>
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}
